<template>
    <div>
        <v-container grid-list-md fluid>
            <v-layout wrap>
                <v-flex xs12 class="d-flex align-center flex-wrap">
                    <h1 class="text-h5 mr-4">Pagamentos</h1>
                    <div class="date-filter mr-4">
                        <DateInterval @onChange="changeDate" today />
                    </div>
                    <v-btn-toggle v-model="filter" mandatory dense color="primary">
                        <v-btn small value="all">Todos</v-btn>
                        <v-btn small value="finished">Finalizados</v-btn>
                        <v-btn small value="inProgress">Em andamento</v-btn>
                    </v-btn-toggle>
                    <v-spacer />
                    <RefreshTimer v-bind="refresh" class="mr-3" />
                    <DownloadCsv :data="csv" name="pagamentos.csv" :labels="csvLabels" delimiter=";" class="mr-16">
                        <v-btn icon color="success" title="Exportar CSV"><v-icon>download</v-icon></v-btn>
                    </DownloadCsv>
                </v-flex>

                <BoardsError class="flex xs12" />

                <v-flex xs12>
                    <div class="totals">
                        <v-card outlined :class="['total-tile', { 'total-selected': !selectedOperatorVisible }]" @click="operatorFilter = null">
                            <div class="caption text-uppercase">Total</div>
                            <div class="text-h6 font-weight-bold">{{ money(total) }}</div>
                        </v-card>
                        <v-card outlined class="total-tile">
                            <div class="caption text-uppercase">Pagamentos</div>
                            <div class="text-h6 font-weight-bold">{{ list.length }}</div>
                        </v-card>
                        <v-card outlined :class="['total-tile', { 'total-selected': alertFilter, 'dashboard-alert': alertTotals.critical > 0 }]" title="Mostrar apenas pagamentos com alerta" @click="alertFilter = !alertFilter">
                            <div class="caption text-uppercase">Com alerta</div>
                            <div class="text-h6 font-weight-bold">
                                {{ alertTotals.count }}
                                <span v-if="alertTotals.critical > 0" class="caption">({{ alertTotals.critical }} crítico{{ alertTotals.critical > 1 ? "s" : "" }})</span>
                            </div>
                        </v-card>
                        <v-card v-for="operator in operators" :key="operator.key" outlined :class="['total-tile', { 'total-selected': operatorFilter == operator.key }]" :title="`${operator.count} pagamento(s)`" @click="toggleOperator(operator.key)">
                            <div class="caption text-uppercase text-truncate">{{ operator.label }}</div>
                            <div class="text-h6 font-weight-bold">{{ money(operator.total) }}</div>
                        </v-card>
                    </div>
                </v-flex>

                <v-flex xs12 v-if="list.length == 0 && alertFilter && items.length > 0" class="text-center grey--text py-8">
                    Nenhum pagamento com alerta no período.
                </v-flex>

                <v-flex xs12 v-if="list.length > 0">
                    <v-data-table :headers="headers" :items="list" :item-class="rowClass" :items-per-page="-1" class="elevation-2" hide-default-footer fixed-header height="calc(100vh - 230px)" :loading="refresh.loading">
                        <v-progress-linear v-slot:progress color="blue" indeterminate />
                        <template v-slot:[`item.charger`]="{ item }">
                            <v-btn small block :href="href(`/now/${item.charger}/${item.plug}`)" target="_blank">
                                <strong>{{ item.charger }}</strong> ({{ item.plug }})
                                <v-spacer />
                                <v-icon small>mdi-open-in-new</v-icon>
                            </v-btn>
                        </template>
                        <template v-slot:[`item.transaction`]="{ item }">{{ transactionId(item) ? `#${transactionId(item)}` : "-" }}</template>
                        <template v-slot:[`item.date`]="{ item }">
                            <span>{{ item.date }}</span>
                        </template>
                        <template v-slot:[`item.status`]="{ item }">
                            <Status :item="item" />
                        </template>
                        <template v-slot:[`item.value`]="{ item }">R$ {{ item.value }}</template>
                        <template v-slot:[`item.card`]="{ item }">
                            <Card :item="item" @click="openpayment(item.id)" />
                        </template>
                    </v-data-table>
                </v-flex>
            </v-layout>
        </v-container>

        <PaymentAction :id="idPayment" @close="idPayment = undefined" />
    </div>
</template>

<script>
import DownloadCsv from "vue-json-csv";

import PaymentStatus from "@/enum/PaymentStatus";
import polling from "@/helpers/polling";
import DateInterval from "@/components/core/DateInterval";
import PaymentAction from "@/components/shared/PaymentAction";
import { paymentAlerts } from "@/helpers/paymentAlerts";
import RefreshTimer from "@/components/shared/RefreshTimer";
import BoardsError from "@/components/shared/BoardsError.vue";

import Card from "./Card";
import Status from "./Status";

const REFRESH_INTERVAL = 50000;
const OTHERS = "others";
const OPERATORS = [
    { key: "stripe", label: "Stripe" },
    { key: "pagarme", label: "Pagar.me (Maquininha)" },
    { key: "mercadopago", label: "Mercado Pago (Cartão)" },
    { key: "mercadopagopix", label: "Mercado Pago (PIX)" },
    { key: "MERCADO_PAGO_EBIKE", label: "EBike" },
    { key: OTHERS, label: "Outros" },
];

function operatorKey(operator) {
    return OPERATORS.some((o) => o.key == operator) ? operator : OTHERS;
}

function sum(list) {
    return list.reduce((total, i) => total + Number.parseFloat(i.value), 0);
}

export default {
    components: { DateInterval, Card, Status, PaymentAction, DownloadCsv, RefreshTimer, BoardsError },

    mixins: [polling(REFRESH_INTERVAL)],

    data() {
        return {
            dates: [],
            items: [],
            idPayment: undefined,
            filter: "all",
            operatorFilter: null,
            alertFilter: false,
            csvLabels: {
                date: "Data",
                charger: "Carregador",
                plug: "Plug",
                status: "Status",
                history: "Sessão",
                transaction: "Transação",
                value: "Valor",
                operator: "Operadora",
                alerts: "Alertas",
            },
        };
    },

    methods: {
        href(url) {
            return `${window.location.origin}${url}`;
        },
        openpayment(id) {
            this.idPayment = id;
        },
        money(value) {
            return `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },
        // Finalizado usa o do histórico, em andamento o da sessão atual
        transactionId(item) {
            if (item.chargerHistory && item.chargerHistory.transactionId) return item.chargerHistory.transactionId;
            if (item.carChargeData && item.carChargeData.transactionId) return item.carChargeData.transactionId;
            return null;
        },
        alertsOf(item) {
            return this.alertsById.get(item.id) || [];
        },
        rowClass(item) {
            const alerts = this.alertsOf(item);
            if (alerts.some((a) => a.critical)) return "payment-row--critical";
            return alerts.length > 0 ? "payment-row--warning" : "";
        },
        toggleOperator(key) {
            this.operatorFilter = this.operatorFilter == key ? null : key;
        },
        changeDate(event) {
            this.dates = event;
            this.refreshNow();
        },
        fetch() {
            if (this.dates.length == 0) return Promise.resolve();

            const query = this.query;
            return this.$http.get(`api/v2/payment${query}`, { continuous: true }).then((result) => {
                // Ignora a resposta se a data mudou durante a requisição, o refreshNow já agendou a nova busca
                if (query == this.query) {
                    this.items = result;
                }
            });
        },
    },

    computed: {
        csv() {
            return this.list.map((i) => {
                return {
                    date: i.date,
                    charger: i.charger,
                    plug: i.plug,
                    value: i.value,
                    status: i.status,
                    history: i.chargerHistory ? `Finalizada com (${i.chargerHistory.status})` : (i.carChargeData ? `Em andamento (${i.carChargeData.status})` : "Erro"),
                    operator: i.operator,
                    transaction: this.transactionId(i),
                    alerts: this.alertsOf(i).map((a) => a.text).join(" | "),
                };
            });
        },
        query() {
            return this.dates.length == 2 ? `?sort[date]=ASC&dateFrom=${this.dates[0]}&dateTo=${this.dates[1]}` : `?dateFrom=${this.dates[0]}&dateTo=${this.dates[0]}`;
        },
        listByStatus() {
            if (this.filter == "finished") {
                return this.items.filter((item) => item.status == PaymentStatus.FINISH);
            } else if (this.filter == "inProgress") {
                return this.items.filter((item) => !item.chargerHistory);
            }
            return this.items;
        },
        alertsById() {
            return new Map(this.items.map((item) => [item.id, paymentAlerts(item)]));
        },
        listByOperator() {
            // Só aplica o filtro de operadora enquanto o bloco dela estiver visível
            const operator = this.operators.find((o) => o.key == this.operatorFilter);
            return operator ? this.listByStatus.filter((i) => operatorKey(i.operator) == operator.key) : this.listByStatus;
        },
        list() {
            return this.alertFilter ? this.listByOperator.filter((i) => this.alertsOf(i).length > 0) : this.listByOperator;
        },
        // Contagem ignora o próprio filtro de alerta para o bloco não zerar ao clicar
        alertTotals() {
            const withAlerts = this.listByOperator.map((i) => this.alertsOf(i)).filter((alerts) => alerts.length > 0);
            return { count: withAlerts.length, critical: withAlerts.filter((alerts) => alerts.some((a) => a.critical)).length };
        },
        // Totais por operadora ignoram o filtro de operadora para os blocos não sumirem ao clicar
        operators() {
            return OPERATORS.map((operator) => {
                const items = this.listByStatus.filter((i) => operatorKey(i.operator) == operator.key);
                return { ...operator, count: items.length, total: sum(items) };
            }).filter((operator) => operator.total > 0);
        },
        selectedOperatorVisible() {
            return this.operators.some((o) => o.key == this.operatorFilter);
        },
        total() {
            return sum(this.list);
        },
        headers() {
            let headers = [
                { text: "Data", value: "date", align: "center" },
                { text: "Carregador", value: "charger", align: "center" },
                { text: "Transação", value: "transaction", align: "center" },
                { text: "Status", value: "status", align: "center" },
                { text: "Valor", value: "value", align: "center" },
                { text: "Cartão", value: "card", align: "center" },
                // { text: "Usuário", value: "user", align: "center" },
            ];

            return headers;
        },
    },
};
</script>

<style src="../AppCharges/shared/alert.css"></style>

<style scoped>
.date-filter {
    width: 230px;
}

.date-filter >>> .v-text-field__details {
    display: none;
}

.date-filter >>> .v-input {
    margin-top: 0;
    padding-top: 0;
}

.totals {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.total-tile {
    flex: 1 1 0;
    min-width: 150px;
    padding: 6px 12px;
}

.v-data-table >>> .payment-row--critical td:first-child {
    box-shadow: inset 4px 0 0 #b71c1c;
}

.v-data-table >>> .payment-row--warning td:first-child {
    box-shadow: inset 4px 0 0 var(--v-warning-base);
}

.total-selected {
    border-color: var(--v-primary-base) !important;
    border-width: 2px;
}
</style>
